import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import './index.css';
import App from './App';

// CRITICAL: Remove manifest immediately if we're on guest-access with apiKey
// This must happen BEFORE React renders to prevent browser from reading manifest
// iOS Chrome uses the current page URL when manifest is not present
(function removeManifestIfGuestAccess() {
  const urlParams = new URLSearchParams(window.location.search);
  const apiKey = urlParams.get('apiKey');
  const isGuestAccess = window.location.pathname === '/guest-access';
  
  if (apiKey && isGuestAccess) {
    // Remove manifest link immediately (synchronously)
    const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    if (manifestLink) {
      manifestLink.remove();
      console.log('🔧 [PRE-RENDER] Removed manifest link to preserve apiKey in URL');
    }
    
    // Also prevent any future manifest links from being added
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.tagName === 'LINK' && (element as HTMLLinkElement).rel === 'manifest') {
              console.log('🔧 [PRE-RENDER OBSERVER] Preventing manifest link from being added');
              element.remove();
            }
          }
        });
      });
    });
    
    observer.observe(document.head, { childList: true, subtree: true });
    
    // Store observer globally so it persists
    (window as any).__guestAccessManifestObserver = observer;
  }
})();

// Register Service Worker manually to ensure correct file is used
// This ensures the Service Worker is registered with the correct path (sw.js) regardless of domain
// Important: Service Worker works with custom domains (admin-staging.youpass.me) as long as:
// 1. The domain is properly configured in Firebase Hosting
// 2. The Service Worker file (sw.js) is accessible at the root
// 3. The scope is set to '/' to work across the entire domain
if ('serviceWorker' in navigator) {
  // Use the current origin (works for both Firebase domain and custom domain)
  const swUrl = `${window.location.origin}/sw.js`;
  
  console.log('🔧 Registering Service Worker:', swUrl);
  console.log('   Current origin:', window.location.origin);
  
  // First, unregister any existing Service Workers that might be using manifest.webmanifest
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      if (registration.active?.scriptURL?.includes('manifest.webmanifest')) {
        console.warn('⚠️  Unregistering incorrect Service Worker:', registration.active.scriptURL);
        registration.unregister();
      }
    }
  });

  // Register the correct Service Worker
  registerSW({
    immediate: true,
    onRegistered(registration) {
      if (registration) {
        console.log('✅ Service Worker registered:', registration);
        console.log('   Scope:', registration.scope);
        const scriptURL = registration.active?.scriptURL || registration.installing?.scriptURL;
        console.log('   Script URL:', scriptURL);
      }
    },
    onRegisterError(error) {
      console.error('❌ Service Worker registration failed:', error);
      // Fallback: try to register manually if auto-registration fails
      console.warn('⚠️  Attempting manual Service Worker registration...');
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((reg) => {
          if (reg) {
            console.log('✅ Manual Service Worker registration successful:', reg);
            console.log('   Scope:', reg.scope);
            const scriptURL = reg.active?.scriptURL || reg.installing?.scriptURL;
            console.log('   Script URL:', scriptURL);
          }
        })
        .catch((err) => {
          console.error('❌ Manual Service Worker registration also failed:', err);
        });
    },
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>
);
